import React, { useMemo } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  Skeleton,
  CircularProgress,
} from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined'
import { poolwerxColors } from '../../../theme/colors'
import { generateInspectorUrl } from '../../../utils/urls'
import { useInspector } from '../../../contexts/InspectorContext'
import {
  deriveOverallHealth,
  bandHex,
  BAND_DESCRIPTION,
  type DomainScore,
} from './healthScore'

const CARD_SX = {
  p: { xs: 2, sm: 2.5 },
  height: '100%',
  borderRadius: 2,
  borderColor: 'grey.200',
  display: 'flex',
  flexDirection: 'column',
}

const OVERLINE_SX = {
  letterSpacing: 0.8,
  fontWeight: 600,
} as const

const CTA_SX = {
  textTransform: 'none',
  fontWeight: 600,
  px: 0,
  minWidth: 0,
  alignSelf: 'flex-start',
  '&:hover': { bgcolor: 'transparent' },
} as const

const RING_SIZE = 112
const RING_THICKNESS = 5
const MAX_SCORE = 5

interface ScoreRingProps {
  score: number | null
  hex: string
  isNoData: boolean
}

const ScoreRing: React.FC<ScoreRingProps> = ({ score, hex, isNoData }) => {
  const value = score === null ? 0 : Math.max(0, Math.min(100, (score / MAX_SCORE) * 100))
  return (
    <Box
      sx={{
        position: 'relative',
        width: RING_SIZE,
        height: RING_SIZE,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <CircularProgress
        variant="determinate"
        value={100}
        size={RING_SIZE}
        thickness={RING_THICKNESS}
        sx={{
          color: 'grey.100',
          position: 'absolute',
          inset: 0,
        }}
      />
      <CircularProgress
        variant="determinate"
        value={value}
        size={RING_SIZE}
        thickness={RING_THICKNESS}
        sx={{
          color: hex,
          position: 'absolute',
          inset: 0,
          '& .MuiCircularProgress-circle': {
            strokeLinecap: 'round',
            transition: 'stroke-dashoffset 0.6s ease-out',
          },
        }}
      />
      <Stack alignItems="center" spacing={0.25}>
        {isNoData ? (
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 800, lineHeight: 1, color: 'text.disabled' }}>
            –
          </Typography>
        ) : (
          <>
            <Typography sx={{ fontSize: '1.85rem', fontWeight: 800, lineHeight: 1, color: hex }}>
              {score?.toFixed(1)}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 0.4 }}>
              / 5
            </Typography>
          </>
        )}
      </Stack>
    </Box>
  )
}

interface BandPillProps {
  band: string
  hex: string
}

const BandPill: React.FC<BandPillProps> = ({ band, hex }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignSelf: 'flex-start',
      alignItems: 'center',
      px: 1.25,
      py: 0.375,
      borderRadius: 999,
      bgcolor: `${hex}15`,
      border: `1px solid ${hex}40`,
      color: hex,
      fontSize: '0.75rem',
      fontWeight: 700,
      letterSpacing: 0.6,
      textTransform: 'uppercase',
    }}
  >
    {band}
  </Box>
)

interface HealthSummaryTileProps {
  fpCode: string
  healthScores?: Record<string, DomainScore>
  loading?: boolean
}

export const HealthSummaryTile: React.FC<HealthSummaryTileProps> = ({
  fpCode,
  healthScores,
  loading = false,
}) => {
  const { franchiseData } = useInspector()
  const overall = useMemo(() => deriveOverallHealth(healthScores), [healthScores])
  const hex = bandHex(overall.band)
  const isNoData = overall.band === 'No Data' || overall.band === 'Not Configured'
  const bandLabel = isNoData ? '-' : overall.band

  return (
    <Paper variant="outlined" sx={CARD_SX}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <MedicalServicesOutlinedIcon sx={{ fontSize: 18, color: poolwerxColors.teal }} />
        <Typography variant="overline" color="text.secondary" sx={OVERLINE_SX}>
          Data Health
        </Typography>
      </Stack>

      {loading ? (
        <Stack spacing={1.5} sx={{ mt: 2, alignItems: 'center' }}>
          <Skeleton variant="circular" width={RING_SIZE} height={RING_SIZE} />
          <Skeleton variant="text" width={80} height={24} />
          <Skeleton variant="text" width="80%" height={20} />
        </Stack>
      ) : (
        <Stack
          direction="row"
          alignItems="center"
          spacing={3}
          sx={{ flex: 1, justifyContent: 'center', py: 1.5 }}
        >
          <ScoreRing score={overall.score} hex={hex} isNoData={isNoData} />
          <Stack spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
            <BandPill band={bandLabel} hex={hex} />
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
              {BAND_DESCRIPTION[overall.band]}
            </Typography>
            <Button
              component={RouterLink}
              to={generateInspectorUrl(fpCode, { id: franchiseData?.id, subpath: 'health' })}
              endIcon={<ArrowForwardIcon />}
              size="small"
              sx={{ ...CTA_SX, mt: 0.25 }}
            >
              Open health report
            </Button>
          </Stack>
        </Stack>
      )}
    </Paper>
  )
}
